import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Modal, Button, Row, Col } from "react-bootstrap";
import { Icon, Form, Heading6, Validator } from "../../widgets";
import { Unit } from "../../../helpers/unit";
import { FormValidator } from "../../../helpers/form-validator";
import { capitalize, isCrewView, isUUID, uuid } from "../../../helpers/common";
import { toDisplayDateString } from "../../../helpers/moment-ex";
import {
	addLogisticsLoad,
	editLogisticsLoad,
	updateLogisticsTareLoad,
	getLogisticsTareLoad,
} from "../../../actions/logistics";
import { getDateRangeInfo } from "../../../actions/analyze";
import { VIEW_CREW, VIEW_EG, VIEW_PLAN } from "../../../constants";

const TareForm = (props) => {
	const {
		load,
		show,
		onHide,
		formType,
		egId,
		crewId,
		isTruck,
	} = props;

	const geoShapeList = useSelector(state => state.equipmentGroup.geoShapeList);
	const { weightShortName } = useSelector(state => state.user.settings);
	const operatorList = useSelector(state => state.person.operatorList);
	const sourceEGList = useSelector(state => state.equipmentGroup.harvesterList);
	const equipmentGroupList = useSelector(state => state.equipmentGroup.equipmentGroupList);
	const farmId = useSelector(state => state.farm.currentFarm.farm_uuid);
	const tareLoadHistory = useSelector(state => state.logistics.tareLoadHistory);
	const crewList = useSelector(state => state.crew.crewList);

	const unit = Unit.getInstance();
	const dispatch = useDispatch();
	const unknownValue = "Unknown";
	const unknownOption = { value: -1, label: unknownValue };

	const [formDetails, setFormDetails] = useState({
		loadActivityId: null,
		srcEGId: null,
		sourceOperatorId: null,
		tarEGId: null,
		operatorId: null,
		unloadSiteId: null,
		storageLocationId: null,
		loadWeight: 0,
		loadTime: "",
		crewId: null,
	});

	const [storageLocations, setStorageLocations] = useState([]);

	const getValidationRules = () => {
		return new FormValidator([
			{
				field: "loadWeight",
				method: "isEmpty",
				validWhen: false,
				message: "Net weight required",
			},
			{
				field: "loadWeight",
				method: "isFloat",
				validWhen: true,
				args: [{ min: 0.1, max: 1000000 }],
				validateEmpty: false,
				message: "Net weight between {min} and {max}",
			},
		]);
	};

	const validator = getValidationRules();
	const [isSaving, setIsSaving] = useState(false);
	const [validation, setValidation] = useState(validator.valid());
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [loadOptions, setLoadOptions] = useState({
		fieldOptions: [],
		crewOptions: [],
		unloadOptions: [],
		storageOptions: [],
		pipeOptions: [],
		sourceEGOptions: [],
		operatorOptions: [],
		transloadoptions: [],
		targetEGOptions: [],
		transportEGOptions: [],
		transportOperatorOptions: [],
	});

	useEffect(() => bindValues(load), [show]);

	const bindValues = (load) => {
		setFormDetails({
			loadActivityId: formType === "add" ? uuid() : load.load_activity_uuid,
			srcEGId:
				!load || !load.source_equipment_group_uuid === "implement_unknown"
					? -1
					: load.source_equipment_group_uuid,
			crewId: !load || !load.crew_uuid === "crew_unknown" ? -1 : load.crew_uuid,
			sourceOperatorId:
				!load || !load.source_operator_uuid === "operator_unknown"
					? -1
					: load.source_operator_uuid,
			tarEGId:
				!load || !load.target_equipment_group_uuid === "transport_unknown"
					? -1
					: load.target_equipment_group_uuid,
			operatorId:
				!load || !load.operator_uuid === "operator_unknown"
					? -1
					: load.operator_uuid,
			unloadSiteId:
				!load || !load.offload_geo_shape_uuid === "offloadsite_unknown"
					? -1
					: load.offload_geo_shape_uuid,
			storageLocationId:
				!load || !load.offload_geo_storage_location_uuid === "storage_unknown"
					? -1
					: load.offload_geo_storage_location_uuid,
			loadWeight:
				!load || !load.load_capacity ? 0 : unit.toWeight(load.load_capacity, 2),
			loadTime: !load || !toDisplayDateString(load.load_date),
		});
	};

	const handleSelect = (options, id) => {
		setFormDetails({
			...formDetails,
			[id]: options && options.value ? options.value : null,
		});
	};

	const handleChange = (event) => {
		setFormDetails({ ...formDetails, [event.target.name]: event.target.value });
	};

	const findCrewDetails = (id, type) => {
		const findCrew = id
			? equipmentGroupList
				.filter((e) => e.crew.crew_uuid === id)
				.map((e) => e.crew)
			: [];
		return findCrew.length > 0
			? type === "name"
				? findCrew[0].name
				: findCrew[0].crew_uuid
			: null;
	};

	const findSourceEGDetails = (id, type) => {
		const sourceEG = id
			? equipmentGroupList.filter((e) => e.equipment_group_uuid === id)
			: [];
		return sourceEG.length > 0
			? type === "name"
				? sourceEG[0].equipment_group_name
				: sourceEG[0].equipment_group_uuid
			: null;
	};

	const findRegionDetails = (id, type) => {
		const findRegion = id
			? geoShapeList.filter((e) => e.geo_shape_uuid === id)
			: [];
		if (!(findRegion.length > 0)) return null;

		if (type === "name") return findRegion[0].geo_region_name;
		if (type === "id") return findRegion[0].geo_region_uuid;
		if (type === "geo_shape_object_uuid")
			return findRegion[0].geo_shape_object_uuid;
		return null;
	};

	const findShapeDetails = (id, type) => {
		const findRegion = id
			? geoShapeList.filter((e) => e.geo_shape_uuid === id)
			: [];
		if (!(findRegion.length > 0)) return null;

		if (type === "name") return findRegion[0].geo_shape_name;
		if (type === "id") return findRegion[0].geo_shape_uuid;
		if (type === "geo_shape_object_uuid")
			return findRegion[0].geo_shape_object_uuid;
		return null;
	};

	const findTargetEGDetails = (id, type) => {
		const tarEG = id
			? equipmentGroupList.filter((e) => e.equipment_group_uuid === id)
			: [];
		return tarEG.length > 0
			? type === "name"
				? tarEG[0].equipment_group_name
				: tarEG[0].equipment_group_uuid
			: null;
	};

	const findStorageDetails = (id, storageLocationId, type) => {
		const findRegion = id
			? geoShapeList.find((e) => e.geo_shape_uuid === id) || {}
			: {};
		const findShape = Array.isArray(findRegion.geo_storage_locations)
			? findRegion.geo_storage_locations.find(
				(e) => e.geo_storage_location_uuid === storageLocationId
			) || {}
			: {};
		return type === "name"
			? findShape.storage_location_name
			: findRegion[0].geo_storage_location_uuid;
	};

	const findOperatorDetails = (id, type) => {
		const findOperator = id
			? operatorList.filter((e) => e.person_uuid === id)
			: [];
		return findOperator.length > 0
			? type === "name"
				? findOperator[0].person_name
				: findOperator[0].person_uuid
			: null;
	};

	const getTareHistory = () => {
		const dateInfo = dispatch(getDateRangeInfo());

		const loadParams = {
			farm_uuid: farmId,
			egId: egId ? egId : null,
			isTruck: egId ? isTruck : false,
			view: egId ? VIEW_EG : crewId ? VIEW_CREW : VIEW_PLAN,
			include_events: true,
			range_type: dateInfo.range,
			from_date: dateInfo.fromDate,
			to_date: dateInfo.toDate,
			is_tareload: true,
			crew_uuid: crewId,
		};

		if (isCrewView(loadParams.view) && crewId && isUUID(crewId))
			loadParams.crew_uuid = crewId;
		dispatch(getLogisticsTareLoad(loadParams));
	};

	const handleSubmit = () => {
		const validation = validator.validate(formDetails);
		setValidation(validation);
		setIsSubmitted(true);

		if (validation.isValid) {
			setIsSaving(true);

			const {
				crewId,
				srcEGId,
				sourceOperatorId,
				unloadSiteId,
				storageLocationId,
				loadWeight,
				loadActivityId,
				tarEGId,
				operatorId,
			} = formDetails;

			let params = {
				farm_uuid: farmId,
				load_activity_uuid: loadActivityId,
				crew_uuid: findCrewDetails(crewId, "id"),
				crew_name: findCrewDetails(crewId, "name"),
				notes: "",
				load_capacity: unit.fromWeight(loadWeight, 2),
				tare_factor: load.tare_factor ? load.tare_factor : 0,
				tare_weight: load.tare_weight ? load.tare_weight : 0,

				offload_geo_shape_uuid:
					unloadSiteId && unloadSiteId !== -1 ? unloadSiteId : null,
				offload_geo_region_uuid: findRegionDetails(unloadSiteId, "id"),
				offload_geo_region_name: findRegionDetails(unloadSiteId, "name"),
				offload_geo_shape_name: findShapeDetails(unloadSiteId, "name"),
				offload_geo_storage_location_uuid:
					storageLocationId && storageLocationId !== -1
						? storageLocationId
						: null,
				offload_geo_storage_location_name: findStorageDetails(
					unloadSiteId,
					storageLocationId,
					"name"
				),

				source_equipment_group_uuid: srcEGId && srcEGId !== -1 ? srcEGId : null,
				source_equipment_group_name: findSourceEGDetails(srcEGId, "name"),
				source_operator_uuid: findOperatorDetails(sourceOperatorId, "id"),
				source_operator_name: findOperatorDetails(sourceOperatorId, "name"),

				target_equipment_group_uuid: findTargetEGDetails(tarEGId, "id"),
				target_equipment_group_name: findTargetEGDetails(tarEGId, "name"),
				operator_uuid: findOperatorDetails(operatorId, "id"),
				operator_name: findOperatorDetails(operatorId, "name"),
			};

			if (formType === "add") {
				dispatch(addLogisticsLoad(params, () => {
					setIsSaving(false);
					getTareHistory();
					onHide();
				}));
			} else if (formType === "edit") {
				let updatedLoadCapacity = 0;

				const updatedData = tareLoadHistory.data.map((load) => {
					if (load.load_activity_uuid === params.load_activity_uuid) {
						updatedLoadCapacity =
							updatedLoadCapacity + Number(params.load_capacity);
						return { ...load, ...params };
					} else {
						updatedLoadCapacity =
							updatedLoadCapacity + Number(load.load_capacity);
						return load;
					}
				});
				dispatch(editLogisticsLoad(params, () => {
					setIsSaving(false);
					dispatch(updateLogisticsTareLoad({
						updatedData,
						updatedLoadCapacity,
					}));
					// getTareHistory();
					onHide();
				}));
			}
		}
	};

	const findItem = (list, key, value) =>
		list.find((item) => item[key] === value);

	const {
		srcEGId,
		sourceOperatorId,
		unloadSiteId,
		loadWeight,
		storageLocationId,
		tarEGId,
		operatorId,
	} = formDetails;

	useEffect(() => {
		const offloadList = geoShapeList.filter(
			(e) => e.geo_region_type_id === 4 && e.active
		);

		if (
			load &&
			load.offload_geo_shape_name !== unknownValue &&
			!findItem(offloadList, "geo_shape_uuid", load.offload_geo_shape_uuid)
		) {
			offloadList.unshift({
				geo_region_name: load.offload_geo_region_name,
				geo_region_type_description: "Offload Site",
				geo_region_type_id: 4,
				geo_region_uuid: load.offload_geo_region_uuid,
				geo_shape_name: load.offload_geo_shape_name,
				geo_shape_uuid: load.offload_geo_shape_uuid,
			});
		}

		const targetOperatorList = operatorList.filter((e) => e.alive);
		if (
			load &&
			load.operator_name !== unknownValue &&
			!findItem(targetOperatorList, "person_uuid", load.operator_uuid)
		) {
			targetOperatorList.unshift({
				person_name: load.operator_name,
				person_uuid: load.operator_uuid,
			});
		}

		const transloadOperator = operatorList.filter((e) => e.alive);
		if (
			load &&
			load.operator_name !== unknownValue &&
			!findItem(transloadOperator, "person_uuid", load.operator_uuid)
		) {
			transloadOperator.unshift({
				person_name: load.operator_name,
				person_uuid: load.operator_uuid,
			});
		}

		const sourceList = equipmentGroupList.filter(
			(e) =>
				e.hasOwnProperty("implement") && e.implement.equipment_type_id === 9
		);
		if (
			load &&
			load.source_equipment_group_name !== unknownValue &&
			!findItem(
				sourceList,
				"equipment_group_uuid",
				load.source_equipment_group_uuid
			)
		) {
			sourceList.unshift({
				equipment_group_name: load.source_equipment_group_name,
				equipment_group_uuid: load.source_equipment_group_uuid,
			});
		}

		const targetEGList = equipmentGroupList.filter(
			(e) =>
				e.alive && (e.vehicle || {}).is_truck && (e.vehicle || {}).is_tare_truck
		);
		if (
			load &&
			load.target_equipment_group_name !== unknownValue &&
			!findItem(
				targetEGList,
				"equipment_group_uuid",
				load.target_equipment_group_uuid
			)
		) {
			targetEGList.unshift({
				equipment_group_name: load.target_equipment_group_name,
				equipment_group_uuid: load.target_equipment_group_uuid,
			});
		}

		/*   const list = equipmentGroupList.filter(e => e.crew.name !== '' && e.alive).map(e => e.crew);
					if (load && !findItem(list, "crew_uuid", load.crew_uuid)) {
							list.unshift({
									crew_uuid: load.crew_uuid,
									crew_name: load.crew_name
							})
					} */

		/* const key = "crew_uuid";
				const crewList = [...new Map(list.map(item => [item[key], item])).values()];
 */
		setLoadOptions({
			sourceEGOptions: [
				unknownOption,
				...sourceList.map((e) => ({
					value: e.equipment_group_uuid,
					label: e.equipment_group_name,
				})),
			],

			// sourceEGOptions: [
			//     unknownOption,
			//     ...equipmentGroupList.filter(e => e.implement.equipment_type_id === 9).map(e => ({
			//         value: e.equipment_group_uuid,
			//         label: e.equipment_group_name
			//     }))
			// ],

			unloadOptions: [
				unknownOption,
				...offloadList.map((e) => ({
					value: e.geo_shape_uuid,
					label: e.geo_shape_name,
				})),
			],

			targetEGOptions: [
				unknownOption,
				...targetEGList.map((e) => ({
					value: e.equipment_group_uuid,
					label: e.equipment_group_name,
				})),
			],

			operatorOptions: [
				unknownOption,
				...transloadOperator.map((e) => ({
					value: e.person_uuid,
					label: e.person_name,
				})),
			],

			storageOptions: [
				unknownOption,
				...storageLocations.map((e) => ({
					value: e.geo_storage_location_uuid,
					label: e.storage_location_name,
				})),
			],

			crewOptions: [
				unknownOption,
				...crewList.map((e) => ({
					value: e.crew_uuid,
					label: e.crew_name,
				})),
			],
		});
	}, [
		show,
		load,
		geoShapeList,
		operatorList,
		equipmentGroupList,
		sourceEGList,
		storageLocations,
		storageLocationId,
	]);

	const validations = isSubmitted
		? validator.validate(formDetails)
		: validation;

	useEffect(() => {
		if (!unloadSiteId || unloadSiteId === unknownOption.value) {
			handleSelect({ value: null }, "storageLocationId");
			setStorageLocations([]);
			return;
		}

		const shape = unloadSiteId
			? (geoShapeList || []).find(
				(shape) => shape.geo_shape_uuid === unloadSiteId
			) || {}
			: {};
		if (!shape.geo_shape_object_uuid) return;

		const storageLocations = Array.isArray(shape.geo_storage_locations)
			? JSON.parse(JSON.stringify(shape.geo_storage_locations))
			: [];
		if (storageLocations.length === 0) {
			storageLocations.push({
				geo_region_shape_uuid: shape.geo_shape_object_uuid,
				geo_storage_location_uuid: "default",
				storage_location_name: "Default",
			});
		}

		if (load) {
			const findStorage = findItem(
				storageLocations,
				"geo_storage_location_uuid",
				load.offload_geo_storage_location_uuid
			);
			if (
				load.offload_geo_storage_location_name !== unknownValue &&
				load.offload_geo_shape_uuid === unloadSiteId &&
				!findStorage
			) {
				storageLocations.unshift({
					storage_location_name: load.offload_geo_storage_location_name,
					geo_storage_location_uuid: load.offload_geo_storage_location_uuid,
				});
			}

			if (!findStorage) {
				const defaultOption = storageLocations[0];
				if (
					load.offload_geo_storage_location_name === unknownValue &&
					load.offload_geo_shape_uuid === unloadSiteId
				)
					handleSelect({ value: null }, "storageLocationId");
				else
					handleSelect(
						{
							value: defaultOption.geo_storage_location_uuid,
							label: defaultOption.storage_location_name,
						},
						"storageLocationId"
					);
			}
		}

		setStorageLocations(storageLocations);
	}, [unloadSiteId, geoShapeList]);

	return (
		<Modal show={show} onHide={onHide} className="modal-form" size="lg">
			<Form className="eg-form">
				<Modal.Header closeButton>
					<Modal.Title>
						<Heading6>
							{formType === "edit" ? "Edit Load" : "Add Load"}
						</Heading6>
					</Modal.Title>
				</Modal.Header>

				<Modal.Body className="py-0">
					<Row className="m-0">
						<Col sm={6} md={6} lg={6}>
							<Form.Group>
								<Form.Label htmlFor="crewName">Crew</Form.Label>
								<Form.Select
									id="crewId"
									placeholder="Select Crew"
									name="crewId"
									onChange={(e) => handleSelect(e, "crewId")}
									value={formDetails.crewId}
									options={loadOptions.crewOptions}
									sortType="asc"
									includeUnknown
								/>
							</Form.Group>
						</Col>
						<Col sm={6} md={6} lg={6}>
							<Form.Group>
								<Form.Label htmlFor="srcEqGroup">Source EG</Form.Label>
								<Form.Select
									id="srcEqGroup"
									placeholder="Select Source Equipment Group"
									name="srcEGId"
									onChange={(e) => handleSelect(e, "srcEGId")}
									value={srcEGId}
									options={loadOptions.sourceEGOptions}
									sortType="asc"
									includeUnknown
								/>
							</Form.Group>
						</Col>

						<Col sm={6} md={6} lg={6}>
							<Form.Group>
								<Form.Label htmlFor="operator">Source EG Operator</Form.Label>
								<Form.Select
									id="operator"
									placeholder="Select Operator"
									name="sourceOperatorId"
									onChange={(e) => handleSelect(e, "sourceOperatorId")}
									value={sourceOperatorId}
									options={loadOptions.operatorOptions}
									sortType="asc"
									includeUnknown
								/>
							</Form.Group>
						</Col>

						<Col sm={6} md={6} lg={6}>
							<Form.Group>
								<Form.Label htmlFor="tarEqGroup">Target EG</Form.Label>
								<Form.Select
									id="tarEqGroup"
									placeholder="Select Target Equipment Group"
									name="tarEGId"
									onChange={(e) => handleSelect(e, "tarEGId")}
									value={tarEGId}
									options={loadOptions.targetEGOptions}
									sortType="asc"
									includeUnknown
								/>
							</Form.Group>
						</Col>

						<Col sm={6} md={6} lg={6}>
							<Form.Group>
								<Form.Label htmlFor="operator">Target EG Operator</Form.Label>
								<Form.Select
									id="operator"
									placeholder="Select Operator"
									name="operatorId"
									onChange={(e) => handleSelect(e, "operatorId")}
									value={operatorId}
									options={loadOptions.operatorOptions}
									sortType="asc"
									includeUnknown
								/>
							</Form.Group>
						</Col>

						<Col sm={6} md={6} lg={6}>
							<Form.Group>
								<Form.Label htmlFor="unloadSite">Offload Site</Form.Label>
								<Form.Select
									id="unloadSite"
									placeholder="Select Offload Site"
									name="unloadSiteId"
									onChange={(e) => handleSelect(e, "unloadSiteId")}
									value={unloadSiteId}
									options={loadOptions.unloadOptions}
									sortType="asc"
									includeUnknown
								/>
							</Form.Group>
						</Col>

						<Col sm={6} md={6} lg={6}>
							<Form.Group>
								<Form.Label htmlFor="storage">Storage</Form.Label>
								<Form.Select
									id="storageLocationId"
									placeholder="Select Storage"
									name="storageLocationId"
									onChange={(e) => handleSelect(e, "storageLocationId")}
									value={storageLocationId}
									options={loadOptions.storageOptions}
									sortType="asc"
									includeUnknown
								/>
							</Form.Group>
						</Col>

						<Col sm={6} md={6} lg={6}>
							<Form.Group>
								<Validator validation={validations.loadWeight}>
									<Form.Label htmlFor="txtName">
										Load Weight ({capitalize(weightShortName)})
										<Validator.Mand />
									</Form.Label>
									<Form.Control
										readOnly={false}
										id="loadWeight"
										value={loadWeight}
										onChange={handleChange}
										min={1}
										max={1000000}
										type="number"
										placeholder="Enter load weight"
										className="form-control"
										autoComplete="off"
										name="loadWeight"
									/>
									<Validator.Text />
								</Validator>
							</Form.Group>
						</Col>
					</Row>
				</Modal.Body>
				<Modal.Footer>
					<Button size="sm" variant="danger" onClick={() => onHide()}>
						<Icon className="fa fa-ban" /> Cancel
					</Button>
					<Button size="sm" variant="primary" onClick={handleSubmit}>
						<Icon className="fa fa-floppy-o" />{" "}
						{isSaving ? "Saving..." : "Save"}
					</Button>
				</Modal.Footer>
			</Form>
		</Modal>
	);
};


export default TareForm;
